/*
 * Luxe Marquee — moteur de défilement continu partagé.
 *
 * Pilote les sliders "luxe" (produits, clients, Instagram) avec une seule
 * implémentation. Une piste = un élément contenant N copies du même contenu ;
 * le moteur translate la piste en boucle et reboucle sur la largeur d'un set.
 *
 * Optimisations clés vs. l'ancien code inline dupliqué :
 *  - une boucle requestAnimationFrame qui ne tourne QUE quand la section est
 *    visible (IntersectionObserver) et l'onglet actif (visibilitychange) ;
 *  - will-change activé uniquement pendant l'animation ;
 *  - respect de prefers-reduced-motion (auto-scroll coupé, drag conservé) ;
 *  - re-mesure au resize (les cartes changent de largeur au breakpoint mobile).
 *
 * Attributs data-* sur la piste :
 *  - data-luxe-marquee : marqueur d'activation (requis)
 *  - data-copies       : nombre de copies du set rendues dans le markup (déf. 2)
 *  - data-speed        : durée en secondes pour parcourir un set (déf. 30)
 *                        -> plus bas = plus rapide
 *  - data-gap          : espace inter-cartes en px, pour le pas des flèches (déf. 0)
 *  - data-hover        : "pause" (arrêt au survol) | "slow" (ralenti) (déf. pause)
 *
 * Flèches optionnelles : placer [data-luxe-prev] / [data-luxe-next] dans la
 * même <section> que la piste.
 */
(function () {
  'use strict';

  // Déjà chargé (plusieurs sections incluent le script) : on se contente
  // de (ré)initialiser les pistes éventuellement nouvelles, puis on sort.
  if (window.LuxeMarquee) {
    window.LuxeMarquee.init(document);
    return;
  }

  var reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var HOVER_SLOW_FACTOR = 0.18; // vitesse au survol en mode "slow"
  var RESUME_DELAY = 2500; // ms avant reprise auto après interaction
  var DRAG_THRESHOLD = 6; // px avant de considérer un drag (vs un clic)

  // Modèle de vitesse unifié : la vitesse courante tend en permanence vers la
  // "vitesse de repos" (croisière, ralenti au survol, ou 0). Toute transition
  // — démarrage, arrêt au survol, inertie d'un flick — est donc lissée, jamais
  // brutale.
  var VEL_EASE = 0.94; // rétention de vitesse par frame @60fps (~0,27s de constante)
  var ARROW_EASE = 0.0024; // easing position des flèches (indépendant du framerate)
  var STOP_EPS = 0.5; // px/s : en dessous, on considère la piste à l'arrêt
  var FLICK_MIN_VELOCITY = 120; // px/s : seuil de déclenchement de l'inertie
  var VELOCITY_SMOOTH = 0.7; // lissage de la vitesse de drag mesurée (EMA)

  function LuxeMarquee(track) {
    this.track = track;
    this.section = track.closest('section') || track.parentNode;

    this.copies = parseInt(track.getAttribute('data-copies'), 10) || 2;
    this.speed = parseFloat(track.getAttribute('data-speed')) || 30;
    this.gap = parseFloat(track.getAttribute('data-gap')) || 0;
    this.hoverMode = track.getAttribute('data-hover') || 'pause';
    this.autoEnabled = !reduceMotion;

    this.pos = 0;
    this.running = false;
    this.lastTime = null;
    this._raf = 0;

    // Visibilité
    this.visible = false;
    this.docHidden = document.hidden;

    // États de pause
    this.hoverPaused = false; // survol en mode "pause"
    this.hoverSlow = false; // survol en mode "slow"
    this.interactPaused = false; // fenêtre de calme après drag/flèche
    this.resumeTimer = null;

    // Animation pilotée (flèches)
    this.animatingTo = false;
    this.targetPos = 0;

    // Drag
    this.isDragging = false;
    this.dragMoved = false;
    this.dragStartX = 0;
    this.dragStartPos = 0;

    // Vitesse courante du modèle unifié (px/s)
    this.vel = 0;
    // Mesure de la vitesse de drag (pour l'inertie au relâchement)
    this.dragVel = 0;
    this.lastMoveX = 0;
    this.lastMoveT = 0;

    this._tickBound = this.tick.bind(this);

    this.measure();
    this.bindPointer();
    this.bindHover();
    this.bindArrows();
    this.observe();
  }

  LuxeMarquee.prototype.measure = function () {
    // gap réel : il peut varier selon le breakpoint (CSS), donc plus fiable
    // que l'attribut data-gap statique.
    var cs = window.getComputedStyle(this.track);
    var g = parseFloat(cs.columnGap || cs.gap);
    if (!isNaN(g)) this.gap = g;

    var sw = this.track.scrollWidth;
    // Période = largeur d'un set + le gap qui le sépare du set suivant.
    // (scrollWidth / copies oublie ce gap -> micro-saut à chaque rebouclage)
    this.setWidth = sw > 0 ? (sw + this.gap) / this.copies : 0;
    var first = this.track.firstElementChild;
    this.cardWidth = first ? first.offsetWidth + this.gap : 0;
    this.autoSpeed = this.setWidth > 0 ? this.setWidth / this.speed : 0; // px/s
  };

  // Vitesse "de repos" vers laquelle l'inertie converge et à laquelle
  // l'auto-défilement tourne (px/s, négatif = vers la gauche).
  LuxeMarquee.prototype.restVelocity = function () {
    if (!this.autoEnabled || this.hoverPaused || this.interactPaused) return 0;
    return -this.autoSpeed * (this.hoverSlow ? HOVER_SLOW_FACTOR : 1);
  };

  LuxeMarquee.prototype.wrap = function (x) {
    var w = this.setWidth;
    if (!w) return 0;
    x = x % w;
    if (x > 0) x -= w;
    return x;
  };

  LuxeMarquee.prototype.render = function () {
    this.track.style.transform = 'translate3d(' + this.pos + 'px,0,0)';
  };

  // Faut-il faire tourner la boucle ?
  LuxeMarquee.prototype.shouldRun = function () {
    if (!this.visible || this.docHidden || this.isDragging) return false;
    if (this.animatingTo) return true; // navigation par flèches en cours
    if (this.restVelocity() !== 0) return true; // en croisière (ou ralenti)
    return Math.abs(this.vel) > STOP_EPS; // encore en train de ralentir vers l'arrêt
  };

  LuxeMarquee.prototype.sync = function () {
    if (this.shouldRun()) this.start();
    else this.stop();
  };

  LuxeMarquee.prototype.start = function () {
    if (this.running) return;
    this.running = true;
    this.lastTime = null;
    this.track.style.willChange = 'transform';
    this._raf = requestAnimationFrame(this._tickBound);
  };

  LuxeMarquee.prototype.stop = function () {
    if (!this.running) return;
    this.running = false;
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = 0;
    this.track.style.willChange = 'auto';
  };

  LuxeMarquee.prototype.tick = function (ts) {
    if (!this.lastTime) this.lastTime = ts;
    var dt = (ts - this.lastTime) / 1000;
    this.lastTime = ts;
    if (dt > 0.1) dt = 0.016; // évite un saut après un onglet en arrière-plan

    if (this.animatingTo) {
      // chemin le plus court vers la cible compte tenu du rebouclage
      var w = this.setWidth;
      var diff = (((this.targetPos - this.pos) % w) + w) % w;
      if (diff > w / 2) diff -= w;
      if (Math.abs(diff) < 0.5) {
        this.pos = this.wrap(this.targetPos);
        this.animatingTo = false;
        this.vel = this.restVelocity();
        this.scheduleResume();
      } else {
        // easing exponentiel indépendant du taux de rafraîchissement
        this.pos = this.wrap(this.pos + diff * (1 - Math.pow(ARROW_EASE, dt)));
      }
    } else {
      // La vitesse tend toujours vers la vitesse de repos : démarrage, arrêt et
      // inertie sont ainsi tous lissés par la même décélération exponentielle.
      var rest = this.restVelocity();
      this.vel = rest + (this.vel - rest) * Math.pow(VEL_EASE, dt * 60);
      if (Math.abs(this.vel - rest) < STOP_EPS) this.vel = rest; // évite une traîne infinie
      this.pos = this.wrap(this.pos + this.vel * dt);
    }

    this.render();

    if (this.shouldRun()) {
      this._raf = requestAnimationFrame(this._tickBound);
    } else {
      this.running = false;
      this._raf = 0;
      this.track.style.willChange = 'auto';
    }
  };

  LuxeMarquee.prototype.scheduleResume = function () {
    var self = this;
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(function () {
      self.interactPaused = false;
      self.sync();
    }, RESUME_DELAY);
  };

  // --- Flèches -------------------------------------------------------------
  LuxeMarquee.prototype.bindArrows = function () {
    var prev = this.section.querySelector('[data-luxe-prev]');
    var next = this.section.querySelector('[data-luxe-next]');
    var self = this;
    if (prev) prev.addEventListener('click', function () { self.nudge(1); });
    if (next) next.addEventListener('click', function () { self.nudge(-1); });
  };

  LuxeMarquee.prototype.nudge = function (dir) {
    var step = this.cardWidth || 280;
    if (reduceMotion) {
      this.pos = this.wrap(this.pos + dir * step);
      this.render();
      return;
    }
    // clics rapides : on prolonge la cible courante (pas de saut)
    var base = this.animatingTo ? this.targetPos : this.pos;
    this.targetPos = base + dir * step;
    this.animatingTo = true;
    this.interactPaused = true;
    clearTimeout(this.resumeTimer);
    this.sync();
  };

  // --- Survol --------------------------------------------------------------
  LuxeMarquee.prototype.bindHover = function () {
    var self = this;
    this.track.addEventListener('mouseenter', function () {
      if (self.isDragging) return;
      if (self.hoverMode === 'slow') self.hoverSlow = true;
      else self.hoverPaused = true;
      self.sync();
    });
    this.track.addEventListener('mouseleave', function () {
      self.hoverSlow = false;
      self.hoverPaused = false;
      self.sync();
    });
  };

  // --- Drag (pointer) ------------------------------------------------------
  LuxeMarquee.prototype.bindPointer = function () {
    var self = this;
    var track = this.track;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      self.isDragging = true;
      self.dragMoved = false;
      self.dragStartX = e.clientX;
      self.dragStartPos = self.pos;
      self.animatingTo = false;
      self.vel = 0;
      self.dragVel = 0;
      self.lastMoveX = e.clientX;
      self.lastMoveT = e.timeStamp || 0;
      clearTimeout(self.resumeTimer);
      self.sync(); // coupe la boucle auto pendant le drag
    });

    document.addEventListener('pointermove', function (e) {
      if (!self.isDragging) return;
      var dx = e.clientX - self.dragStartX;
      if (!self.dragMoved && Math.abs(dx) > DRAG_THRESHOLD) {
        self.dragMoved = true;
        track.classList.add('is-dragging');
      }
      if (self.dragMoved) {
        self.pos = self.wrap(self.dragStartPos + dx);
        self.render(); // rendu direct : la boucle ne tourne pas pendant le drag

        // mesure de la vitesse pour l'inertie (px/s, lissée)
        var t = e.timeStamp || 0;
        var mdt = (t - self.lastMoveT) / 1000;
        if (mdt > 0) {
          if (mdt > 0.1) {
            self.dragVel = 0; // pause en plein drag = pas d'élan
          } else {
            var inst = (e.clientX - self.lastMoveX) / mdt;
            self.dragVel = self.dragVel * VELOCITY_SMOOTH + inst * (1 - VELOCITY_SMOOTH);
          }
        }
        self.lastMoveX = e.clientX;
        self.lastMoveT = t;
      }
    });

    function endDrag() {
      if (!self.isDragging) return;
      self.isDragging = false;
      track.classList.remove('is-dragging');
      if (!reduceMotion && Math.abs(self.dragVel) > FLICK_MIN_VELOCITY) {
        // flick rapide -> on injecte la vitesse ; elle se fond dans la croisière
        self.vel = self.dragVel;
        self.interactPaused = false;
        clearTimeout(self.resumeTimer);
      } else {
        // drag lent -> positionnement : arrêt en douceur puis reprise différée
        self.vel = 0;
        self.interactPaused = true;
        self.scheduleResume();
      }
      self.sync();
    }
    document.addEventListener('pointerup', endDrag);
    document.addEventListener('pointercancel', endDrag);

    // Empêche la navigation du lien quand l'utilisateur a réellement glissé
    track.addEventListener(
      'click',
      function (e) {
        if (self.dragMoved) {
          e.preventDefault();
          e.stopPropagation();
          self.dragMoved = false;
        }
      },
      true
    );
  };

  // --- Visibilité ----------------------------------------------------------
  LuxeMarquee.prototype.observe = function () {
    var self = this;
    if ('IntersectionObserver' in window) {
      this._io = new IntersectionObserver(
        function (entries) {
          self.visible = entries[0].isIntersecting;
          self.sync();
        },
        { rootMargin: '100px' }
      );
      this._io.observe(this.section);
    } else {
      this.visible = true;
      this.sync();
    }

    document.addEventListener('visibilitychange', function () {
      self.docHidden = document.hidden;
      self.sync();
    });

    // Premier rendu (utile en reduced-motion : pose la transform initiale)
    this.render();
  };

  LuxeMarquee.prototype.remeasure = function () {
    var prevSet = this.setWidth;
    this.measure();
    // conserve la progression relative dans le set après changement de largeur
    if (prevSet) this.pos = this.wrap((this.pos / prevSet) * this.setWidth);
    this.render();
  };

  // --- Init / cycle de vie -------------------------------------------------
  var instances = [];
  var resizeTimer = null;

  function init(root) {
    var scope = root || document;
    var tracks = scope.querySelectorAll('[data-luxe-marquee]');
    for (var i = 0; i < tracks.length; i++) {
      var t = tracks[i];
      if (t.__luxeMarquee) continue;
      t.__luxeMarquee = new LuxeMarquee(t);
      instances.push(t.__luxeMarquee);
    }
  }

  function remeasureAll() {
    for (var i = 0; i < instances.length; i++) instances[i].remeasure();
  }

  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(remeasureAll, 200);
  });

  // Re-mesure après chargement complet des images (filet de sécurité).
  window.addEventListener('load', remeasureAll);

  // Éditeur de thème Shopify : (ré)init quand une section est (re)chargée.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var t = e.target.querySelector('[data-luxe-marquee]');
    if (t && t.__luxeMarquee) t.__luxeMarquee.stop();
  });

  LuxeMarquee.init = init;
  window.LuxeMarquee = LuxeMarquee;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();
